import { Injectable } from '@angular/core';
import { TreoNavigationItem } from '@treo/components/navigation/navigation.types';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserService } from '../../../app/core';

@Injectable({
  providedIn: 'root'
})
export class TreoNavigationService {
  // Private
  private _componentRegistry: Map<string, any>;
  private _navigationStore: Map<string, TreoNavigationItem[]>;

  /**
   * Constructor
   */
  constructor(private userService: UserService) {
    // Set the private defaults
    this._componentRegistry = new Map<string, any>();
    this._navigationStore = new Map<string, any>();
  }

  // Private
  private _onNavigationChanged: BehaviorSubject<any> = new BehaviorSubject<any>(true);

  /**
   * Get onNavigationChanged
   *
   * @returns Observable<any> it will return observable of navigation changes
   */
  get onNavigationChanged(): Observable<any> {
    return this._onNavigationChanged.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Register navigation component
   *
   * @param name
   * @param component
   */
  registerComponent(name: string, component: any): void {
    this._componentRegistry.set(name, component);
  }

  /**
   * Deregister navigation component
   *
   * @param name
   */
  deregisterComponent(name: string): void {
    this._componentRegistry.delete(name);
  }

  /**
   * Get navigation component from the registry
   *
   * @param name
   */
  getComponent(name: string): any {
    return this._componentRegistry.get(name);
  }

  /**
   * Store the given navigation with the given key
   *
   * @param key
   * @param navigation
   */
  storeNavigation(key: string, navigation: TreoNavigationItem[]): void {
    // Add to the store
    this._onNavigationChanged.next(key);
    this._navigationStore.set(key, navigation);
  }

  /**
   * Get navigation from storage by key
   *
   * @param key
   * @returns {any}
   */
  getNavigation(key: string): TreoNavigationItem[] {
    return this.getAllowedNavigation(this._navigationStore.get(key));
  }

  getAllowedNavigation(navigation: TreoNavigationItem[]): TreoNavigationItem[] {
    const allowedUIMenus = this.userService.getUser().allowedUiMenus;
    return (navigation || [])
      .map((item) => {
        return this.setIsHidden(item, allowedUIMenus);
      });
  }

  setIsHidden(item: TreoNavigationItem, allowedUIMenus: string[]): TreoNavigationItem {
    switch (item.type) {
      case 'aside':
        (item.children || []).map((child) => {
          return this.setIsHidden(child, allowedUIMenus);
        });
        item.hidden = (item.children || []).every(child => child.hidden);
        break;
      case 'basic':
        item.hidden = !allowedUIMenus.includes(item.id);
        break;
      case 'collapsable':
        (item.children || []).map((child) => {
          return this.setIsHidden(child, allowedUIMenus);
        });
        item.hidden = (item.children || []).every(child => child.hidden);
        break;
      case 'divider':
        break;
      case 'group':
        (item.children || []).map((child) => {
          return this.setIsHidden(child, allowedUIMenus);
        });
        item.hidden = (item.children || []).every(child => child.hidden);
        break;
      case 'spacer':
        break;
    }
    return item;
  }

  /**
   * Delete the navigation from the storage
   *
   * @param key
   */
  deleteNavigation(key: string): void {
    // Check if the navigation exists
    if (!this._navigationStore.has(key)) {
      console.warn(`Navigation with the key '${key}' does not exist in the store.`);
    }else {

      // Delete from the storage
      this._navigationStore.delete(key);
    }
  }

  /**
   * Utility function that returns a flattened
   * version of the given navigation array
   *
   * @param navigation
   * @param flatNavigation
   * @returns {TreoNavigationItem[]}
   */
  getFlatNavigation(navigation: TreoNavigationItem[], flatNavigation: TreoNavigationItem[] = []): TreoNavigationItem[] {
    for (const item of navigation) {
      if (item.type === 'basic') {
        flatNavigation.push(item);
        continue;
      }

      if (item.type === 'aside' || item.type === 'collapsable' || item.type === 'group') {
        if (item.children) {
          this.getFlatNavigation(item.children, flatNavigation);
        }
      }
    }

    return flatNavigation;
  }

  /**
   * Utility function that returns the item
   * with the given id from given navigation
   *
   * @param id
   * @param navigation
   */
  getItem(id: string, navigation: TreoNavigationItem[]): TreoNavigationItem | null {
    for (const item of navigation) {
      if (item.id === id) {
        return item;
      }

      if (item.children) {
        const childItem = this.getItem(id, item.children);

        if (childItem) {
          return childItem;
        }
      }
    }

    return null;
  }

  /**
   * Utility function that returns the item's parent
   * with the given id from given navigation
   *
   * @param id
   * @param navigation
   * @param parent
   */
  getItemParent(
    id: string,
    navigation: TreoNavigationItem[],
    parent: TreoNavigationItem[] | TreoNavigationItem
  ): TreoNavigationItem[] | TreoNavigationItem | null {
    for (const item of navigation) {
      if (item.id === id) {
        return parent;
      }

      if (item.children) {
        const childItem = this.getItemParent(id, item.children, item);

        if (childItem) {
          return childItem;
        }
      }
    }

    return null;
  }
}
